import { Button, InputNumber, Form, Table, AutoComplete } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const circleButtonBase = `
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
`;

// ─── Page layout ────────────────────────────────────────────────────────────

export const PageWrapper = styled.div`
    width: 100%;
    padding: 10px;
`;

export const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
`;

export const PageTitle = styled.h2`
    color: #7bad7e;
    margin: 0 0 4px 0;
    font-size: 20px;
    font-weight: 600;
`;

export const PageSubtitle = styled.p`
    margin: 0;
    color: #8c8c8c;
    font-size: 13px;
`;

export const InfoBanner = styled.div`
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #7bad7e;
    border-radius: 6px;
    padding: 10px 16px;
    margin-bottom: 20px;
    color: #424647;
    font-size: 13px;
`;

export const BannerCode = styled.code`
    background: rgba(123, 173, 126, 0.1);
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 12px;
`;

// ─── Table ───────────────────────────────────────────────────────────────────

export const StyledTable = styled(Table)`
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 3px 0px 20px rgba(0, 0, 0, 0.06);
`;

export const ConfigLabelText = styled.span`
    font-weight: 600;
    color: #171c2f;
`;

export const TagList = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

export const TemplateCode = styled.code`
    font-size: 12px;
    color: #424647;
    word-break: break-all;
`;

export const MaxCharsText = styled.span`
    font-weight: 500;
    color: #424647;
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const AddButton = styled(Button)`
    background-color: #7bad7e;
    border-color: #7bad7e;
    color: #fff;
    font-weight: 500;

    &:hover,
    &:focus {
        background-color: #6a9a6d;
        border-color: #6a9a6d;
        color: #fff;
    }
`;

export const EditButton = styled(Button)`
    ${circleButtonBase}
    background-color: #7bad7e;
    border-color: #7bad7e;
    color: #fff;

    &:hover,
    &:focus {
        background-color: #6a9a6d;
        border-color: #6a9a6d;
        color: #fff;
    }
`;

export const DeleteButton = styled(Button)`
    ${circleButtonBase}
    background-color: #ff4d4f;
    border-color: #ff4d4f;
    color: #fff;

    &:hover,
    &:focus {
        background-color: #e03e40;
        border-color: #e03e40;
        color: #fff;
    }
`;

export const SaveButton = styled(Button)`
    background-color: #7bad7e;
    border-color: #7bad7e;
    color: #fff;
    min-width: 100px;

    &:hover,
    &:focus {
        background-color: #6a9a6d;
        border-color: #6a9a6d;
        color: #fff;
    }
`;

// ─── Tags ────────────────────────────────────────────────────────────────────

export const PharmacyTag = styled.span`
    display: inline-flex;
    align-items: center;
    background-color: #7bad7e;
    border-radius: 12px;
    padding: 2px 10px;
    color: #fff;
    font-size: 12px;
    margin: 2px;
    white-space: nowrap;
`;

export const PharmacyTagState = styled.span`
    opacity: 0.85;
    margin-left: 2px;
`;

export const PharmacyTagId = styled.span`
    opacity: 0.65;
    font-size: 11px;
    margin-left: 4px;
`;

export const PharmacyTagRemove = styled.span`
    cursor: pointer;
    opacity: 0.75;
    font-size: 11px;
    line-height: 1;
    margin-left: 4px;

    &:hover {
        opacity: 1;
    }
`;

export const IdTag = styled.span`
    display: inline-block;
    background-color: rgba(123, 173, 126, 0.12);
    border: 1px solid rgba(123, 173, 126, 0.35);
    border-radius: 10px;
    padding: 1px 8px;
    color: #3d6b40;
    font-family: monospace;
    font-size: 12px;
    margin: 2px;
    white-space: nowrap;
`;

// ─── Modal ───────────────────────────────────────────────────────────────────

export const ModalTitle = styled.span`
    color: #171c2f;
    font-weight: 600;
`;

export const PharmacyTagList = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

export const FullWidthAutoComplete = styled(AutoComplete)`
    width: 100%;
`;

export const TemplateFormItem = styled(Form.Item)`
    margin-bottom: 4px;
`;

export const MaxCharsInput = styled(InputNumber)`
    width: 160px;
`;

export const CharCountRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 16px;
`;

export const HelpText = styled.span`
    font-size: 12px;
    color: #8c8c8c;
`;

export const CharCount = styled.span`
    font-size: 12px;
    white-space: nowrap;
    margin-left: 12px;
    color: ${({ $color }) => $color || '#8c8c8c'};
`;

export const CharCountRemaining = styled.span`
    margin-left: 4px;
    opacity: 0.8;
`;

export const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
`;

// ─── Shared ──────────────────────────────────────────────────────────────────

export const FieldLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #8c8c8c;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

export const TooltipIcon = styled(QuestionCircleOutlined)`
    color: #7bad7e;
    cursor: pointer;
    font-size: 13px;
`;

// ─── Placeholder tooltip ─────────────────────────────────────────────────────

export const TooltipContent = styled.div`
    max-width: 420px;
    color: #fff;
`;

export const TooltipTable = styled.table`
    margin-top: 8px;
    border-collapse: collapse;
    width: 100%;
`;

export const TooltipPlaceholderCell = styled.td`
    padding-right: 12px;
    padding-bottom: 4px;
    white-space: nowrap;
`;

export const TooltipDescCell = styled.td`
    padding-bottom: 4px;
    color: #fff;
`;

export const TooltipCode = styled.code`
    background: rgba(255, 255, 255, 0.15);
    padding: 1px 4px;
    border-radius: 3px;
    color: #fff;
    font-size: ${({ $small }) => ($small ? '12px' : 'inherit')};
`;

export const TooltipExample = styled.div`
    margin-top: 8px;
`;

export const TooltipMutedText = styled.span`
    color: rgba(255, 255, 255, 0.65);
`;

export const TooltipStrongText = styled.strong`
    color: #fff;
`;

export const PopconfirmSubtitle = styled.div`
    color: #666;
    font-weight: normal;
    font-size: 12px;
    margin-top: 2px;
`;
